const ColorDropdownEnhancer = globalThis.ColorDropdownEnhancer;

class VariantSelects extends HTMLElement {
  constructor() {
    super();

    const media_template = this.dataset.mediaTemplate || 'default';

    this.config = {
      media_template: media_template
    }

    this.addEventListener('change', this.onVariantChange);
    this.setupColorDropdowns();
  }

  onVariantChange(e) {
    const { isCombinedProduct, targetUrl } = this.combinedListingCheck(e);

    this.toggleAddButton(true, '', false);

    if (isCombinedProduct) {
      this.fetchProductSection(targetUrl);
      return;  // Stop execution if false
    }

    this.updateOptions();
    this.updateMasterId();
    this.updatePickupAvailability();
    this.updateInventoryStatus();
    this.removeErrorMessage();
    this.updateVariantStatuses();
    this.updateProductDetails();
    this.updateQuantityLimits();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      if (this.config.media_template === 'selected_group') {
        this.fetchGallery();
      } else {
        this.updateMedia();
      }
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }

    this.syncColorDropdowns();

    document.dispatchEvent(
      new CustomEvent('loyalty-ea:refresh', {
        detail: {
          scope: this,
          variantId: this.currentVariant ? String(this.currentVariant.id) : undefined,
        },
      }),
    );
  }

  updateProductDetails() {
    // Use the Shopify Section Rendering API to fetch and update the product details snippet.
    if (!this.currentVariant) return;

    const selector = '[data-product-details]';
    const detailsBySection = Array.from(document.querySelectorAll(selector)).reduce((sections, detailsElem) => {
      const sectionWrapper = detailsElem.closest('.shopify-section');
      if (!sectionWrapper?.id) return sections;

      // API expects section id without the `shopify-section-` prefix (same as fetchGallery).
      const sectionId = sectionWrapper.id.replace(/^shopify-section-/, '');
      if (!sectionId) return sections;

      if (!sections.has(sectionId)) {
        sections.set(sectionId, []);
      }

      sections.get(sectionId).push(detailsElem);
      return sections;
    }, new Map());

    if (detailsBySection.size === 0) return;

    detailsBySection.forEach((detailsElems, sectionId) => {
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}&variant=${this.currentVariant.id}`;

      fetch(url)
        .then((res) => {
          if (!res.ok) {
            throw new Error(`Section fetch failed: ${res.status}`);
          }
          return res.text();
        })
        .then((html) => {
          const dom = new DOMParser().parseFromString(html, 'text/html');
          const newDetails = dom.querySelector(selector);
          if (!newDetails) return;

          detailsElems.forEach((detailsElem) => {
            detailsElem.innerHTML = newDetails.innerHTML;
          });
        })
        .catch((err) => {
          console.error('updateProductDetails:', err);
        });
    });
  }

  combinedListingCheck(e) {
    const currentUrl = window.location.pathname;
    const targetUrl = e.target.dataset.productUrl;
    let isCombinedProduct = false;

    if (targetUrl != undefined && currentUrl != targetUrl) {
      isCombinedProduct = true;
    }

    return { isCombinedProduct, targetUrl };
  }

  getSelectedValue(wrapper) {
    if (!wrapper) return '';
    const select = wrapper.querySelector('select');
    if (select) {
      const selected = select.options[select.selectedIndex];
      return selected ? selected.value : '';
    }
    const checked = wrapper.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : '';
  }

  getOptionWrappers(selector = '.product-form__input') {
    // Variant JSON keeps Shopify's original option order, even if the picker is rendered in a different visual order.
    return Array.from(this.querySelectorAll(selector))
      .map((wrapper, index) => {
        const position = Number(wrapper.dataset.optionPosition);
        return {
          wrapper,
          index: Number.isFinite(position) && position > 0 ? position - 1 : index
        };
      })
      .sort((a, b) => a.index - b.index)
      .map(({ wrapper }) => wrapper);
  }

  updateLegendsForFieldsets() {
    this.querySelectorAll('fieldset.product-form__input').forEach((fieldset) => {
      const legend = fieldset.querySelector('legend [data-variant-legend]');
      const radios = fieldset.querySelectorAll('input[type="radio"]');
      const selectedOption = Array.from(radios).find((radio) => radio.checked);
      if (legend && selectedOption) {
        legend.textContent = selectedOption.getAttribute('value');
      }
    });
  }

  async fetchProductSection(newProductUrl) {
    const productElement = this.closest('.main-product');
    const productSectionId = this.dataset.section;
    let params = `section_id=${productSectionId}&`;

    const wrappers = this.getOptionWrappers();
    const selectedOptionValues = wrappers
      .map((wrapper) => {
        const select = wrapper.querySelector('select');
        if (select) {
          const selected = select.options[select.selectedIndex];
          return selected?.dataset?.optionValueId;
        }
        const checked = wrapper.querySelector('input[type="radio"]:checked');
        return checked?.dataset?.optionValueId;
      })
      .filter(Boolean)
      .join(',');
    params += `option_values=${selectedOptionValues}`;

    fetch(`${newProductUrl}?${params}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        productElement.innerHTML = html.querySelector('.main-product').innerHTML;
        window.history.replaceState({ }, '', `${newProductUrl}`);
      });
  }

  updateOptions() {
    const wrappers = this.getOptionWrappers();
    this.options = wrappers.map((wrapper) => this.getSelectedValue(wrapper));
    this.updateLegendsForFieldsets();
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  fetchGallery() {
    const mainProductSection = document.querySelector('.shopify-section.main-product');
    if (!mainProductSection) return;

    const mainProductSectionId = mainProductSection.getAttribute('id').replace('shopify-section-', '');

    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${mainProductSectionId}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const productMedia = document.querySelector(`#${mainProductSectionId} [data-pdp-media]`);
        const zoomModalBody = document.querySelector(`.modal--fullscreen.zoom-gallery [data-modal-body]`);
        const newProductMedia = html.querySelector(`#${mainProductSectionId} [data-pdp-media]`);
        const newZoomModalBody = html.querySelector(`.modal--fullscreen.zoom-gallery [data-modal-body]`);

        if (newProductMedia && newZoomModalBody && productMedia && zoomModalBody) {
          productMedia.innerHTML = newProductMedia.innerHTML;
          zoomModalBody.innerHTML = newZoomModalBody.innerHTML;
        }
      })
      .catch((error) => {
        console.error('There was a problem with the fetch operation:', error);
      });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGridySlider = document.getElementById(`slider-${this.dataset.section}`);
    const thumbnailsGridySlider = document.getElementById(`thumbnail-slider-${this.dataset.section}`);
    
    if (mediaGridySlider) {
      mediaGridySlider.scrollToSlideByID(
        `item-${this.currentVariant.featured_media.id}`
      );
    }
    
    if (thumbnailsGridySlider) {
      thumbnailsGridySlider.scrollToSlideByID(`thumbnail-item-${this.currentVariant.featured_media.id}`)
    }

    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    const isGrid = mediaGridySlider ? mediaGridySlider.dataset.desktopEnabled === 'false' : false;
    if (isDesktop && isGrid) {
      document.getElementById(`item-${this.currentVariant.featured_media.id}`)?.scrollIntoView();
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}-product-buy-bar`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  getQuantityInputs() {
    return Array.from(document.querySelectorAll(`quantity-input [data-quantity-input-field][form="product-form-${this.dataset.section}"]`));
  }

  getQuantityRules(variant) {
    const resolvedVariant = variant || this.currentVariant;
    const inventoryData = globalThis.inventories?.[this.dataset.productId]?.[resolvedVariant?.id];
    if (Woolman?.QuantityLimits?.getVariantRules) {
      return Woolman.QuantityLimits.getVariantRules(resolvedVariant, inventoryData);
    }

    return {
      min: 1,
      max: null,
      disabled: !resolvedVariant || resolvedVariant.available === false
    };
  }

  updateQuantityLimits() {
    const quantityInputs = this.getQuantityInputs();
    if (quantityInputs.length === 0) return;

    const rules = this.getQuantityRules();
    quantityInputs.forEach((quantityInput) => {
      if (Woolman?.QuantityLimits?.applyRulesToInput) {
        Woolman.QuantityLimits.applyRulesToInput(quantityInput, rules);
      }
    });
  }

  updateVariantStatuses() {
    const inputWrappers = this.getOptionWrappers();
    if (inputWrappers.length === 0) return;

    const firstValue = this.getSelectedValue(inputWrappers[0]);
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => variant.option1 === firstValue
    );

    inputWrappers.forEach((wrapper, index) => {
      if (index === 0) return;
      const optionInputs = [...wrapper.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = this.getSelectedValue(inputWrappers[index - 1]);
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter(
          (variant) =>
            variant.available && variant[`option${index}`] === previousOptionSelected
        )
        .map((variantOption) => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      const value = input.getAttribute('value');
      const isAvailable = listOfAvailableOptions.includes(value);

      if (input.tagName === 'OPTION') {
        input.dataset.optionDisabled = isAvailable ? 'false' : 'true';
        if (isAvailable) {
          input.innerText = value;
        } else {
          input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', value);
        }
      } else {
        if (isAvailable) {
          input.classList.remove('disabled');
        } else {
          input.classList.add('disabled');
        }
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  updateInventoryStatus() {
    this.productInventoryNode = document.querySelector('[data-product-inventory]');

    if (!this.productInventoryNode || !window.inventories || !window.inventories[this.dataset.productId]) return;

    this.inventoryThreshold = parseInt(this.productInventoryNode.dataset.threshold);
    this.showQuantityInStock = this.productInventoryNode.dataset.showQuantityInStock;

    const variantInventoryObject = window.inventories[this.dataset.productId][this.currentVariant?.id];

    if (!this.currentVariant || !this.currentVariant.inventory_management) {
      this.toggleInventoryQuantity(false);
      this.toggleIncomingInventory(false);

      return;
    }

    if (this.currentVariant.inventory_management === 'shopify') {
      if (variantInventoryObject.quantity <= 0 && variantInventoryObject.incoming === 'true' && variantInventoryObject.quantity < this.inventoryThreshold) {
        this.toggleInventoryQuantity(false);
        this.toggleIncomingInventory(true, variantInventoryObject.next_incoming_date);
      } else {
        this.toggleInventoryQuantity(true, variantInventoryObject.quantity);
        this.toggleIncomingInventory(false);
      }
    }
  }

  toggleInventoryQuantity(show, quantity) {
    if (show === false) {
      this.productInventoryNode.classList.add('hide');
      return;
    }

    this.productInventoryNode.classList.remove('inventory-status--in-stock', 'inventory-status--low', 'inventory-status--sold-out', 'inventory-status--sold-out-continue-selling');

    if (this.currentVariant && this.currentVariant.available && parseInt(quantity) <= parseInt(this.inventoryThreshold) && parseInt(quantity) > 0) {
      this.productInventoryNode.classList.add('inventory-status--low');
      if (this.showQuantityInStock === 'true') {
        this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.few_left_with_quantity.replace('[quantity]', quantity);
      } else {
        this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.few_left;
      }
    } else if (this.currentVariant && this.currentVariant.available && parseInt(quantity) > parseInt(this.inventoryThreshold)) {
      this.productInventoryNode.classList.add('inventory-status--in-stock');
      if (this.showQuantityInStock === 'true') {
        this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.in_stock_with_quantity.replace('[quantity]', quantity);
      } else {
        this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.in_stock;
      }
    } else if (this.currentVariant && this.currentVariant.available && parseInt(quantity) < 1) {
      this.productInventoryNode.classList.add('inventory-status--sold-out-continue-selling');
      this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.out_of_stock_continue_selling;
    } else {
      this.productInventoryNode.classList.add('inventory-status--sold-out');
      this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.out_of_stock;
    }

    this.productInventoryNode.classList.remove('hide');
  }

  toggleIncomingInventory(show, date) {
    const incomingInventoryNode = document.querySelector('[data-incoming-inventory]');
    if (!incomingInventoryNode) return;

    if (show == false && incomingInventoryNode) {
      incomingInventoryNode.classList.add('hide');

      return;
    }

    let string = !date ?
      window.variantStrings.inventory.waiting_for_stock : 
      window.variantStrings.inventory.will_be_in_stock_after.replace('[date]', date);

    incomingInventoryNode.classList.remove('hide');
    incomingInventoryNode.firstElementChild.textContent = string;
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const skuSource = html.getElementById(
          `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const destinationBuyBarInfo = document.getElementById(
          `buy-bar-info-${this.dataset.section}`
        );
        const source = html.getElementById(`price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const buyBarInfoSource = html.getElementById(`buy-bar-info-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        if (source && destination) destination.innerHTML = source.innerHTML;
        if (source && destinationBuyBarInfo) destinationBuyBarInfo.innerHTML = buyBarInfoSource.innerHTML;

        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('hidden', skuSource.classList.contains('hidden'));
        }

        // PayPal Messaging — the [data-pp-message] block lives in the
        // product-blocks loop, outside the price/sku/buy-bar containers
        // that this swap targets, so its data-pp-amount would otherwise
        // stay frozen at the initially-selected variant's price. Re-read
        // it from the fetched section HTML (server-formatted via
        // `money_without_currency`, so locale + money_format stay correct)
        // and write it back. The PayPal Messaging SDK observes
        // data-pp-* attribute mutations and re-renders automatically.
        const ppMessageSource = html.querySelector('[data-pp-message]');
        const ppMessageDestination = document.querySelector('[data-pp-message]');
        if (ppMessageSource && ppMessageDestination) {
          const newPpAmount = ppMessageSource.getAttribute('data-pp-amount');
          if (newPpAmount !== null) {
            ppMessageDestination.setAttribute('data-pp-amount', newPpAmount);
          }
        }

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('hidden');
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
        document.dispatchEvent(
          new CustomEvent('loyalty-ea:refresh', {
            detail: {
              scope: this,
              variantId: String(this.currentVariant.id),
            },
          }),
        );
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    const multiplyButtons = productForm.querySelectorAll('.btn--add-multiplier');
    if (!addButton) return;

    const addToBagIconHTML = `
    <svg class="cart-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 6L6 2H18L21 6M3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6M3 6H21M16 10C16 11.0609 15.5786 12.0783 14.8284 12.8284C14.0783 13.5786 13.0609 14 12 14C10.9391 14 9.92172 13.5786 9.17157 12.8284C8.42143 12.0783 8 11.0609 8 10"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      />
    </svg>
    `

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      addButton.dataset.shopifyDisabled = 'true';
      if (text) addButtonText.textContent = text;
      if (multiplyButtons) {
        multiplyButtons.forEach(button => {
          button.disabled = true;
        });
      }
    } else {
      addButton.removeAttribute('disabled');
      delete addButton.dataset.shopifyDisabled;
      if (this.dataset.isPreorder == 'true') {
        addButtonText.textContent = window.variantStrings.preOrder;
      } else {
        addButtonText.innerHTML = addToBagIconHTML + `<span class="size--regular weight--medium">${window.variantStrings.addToCart}</span>`;
      }
      if (multiplyButtons) {
        multiplyButtons.forEach(button => {
          button.removeAttribute('disabled');
        });
      }
    }

    const productForm_BuyBar = document.getElementById(
      `product-form-${this.dataset.section}-product-buy-bar`
    );
    if (productForm_BuyBar) {
      const addButton_BuyBar = productForm_BuyBar.querySelector('[name="add"]');
      const addButtonText_BuyBar = productForm_BuyBar.querySelector(
        '[name="add"] > span'
      );

      if (disable) {
        addButton_BuyBar.setAttribute("disabled", "disabled");
        addButton_BuyBar.dataset.shopifyDisabled = 'true';
        if (text) addButtonText_BuyBar.textContent = text;
      } else {
        addButton_BuyBar.removeAttribute("disabled");
        delete addButton_BuyBar.dataset.shopifyDisabled;
        if (this.dataset.isPreorder == "true") {
          addButtonText_BuyBar.textContent = window.variantStrings.preOrder;
        } else {
          addButtonText_BuyBar.textContent = window.variantStrings.addToCart;
        }
      }
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('hidden');

    const button_BuyBar = document.getElementById(
      `product-form-${this.dataset.section}-product-buy-bar`
    );
    if (!button_BuyBar) return;
    const addButton_BuyBar = button_BuyBar.querySelector('[name="add"]');
    const addButtonText_BuyBar = button_BuyBar.querySelector(
      '[name="add"] > span'
    );
    const price_BuyBar = document.getElementById(
      `price-${this.dataset.section}-product-buy-bar`
    );
    if (!addButton_BuyBar) return;
    addButtonText_BuyBar.textContent = window.variantStrings.unavailable;
    if (price_BuyBar) price_BuyBar.classList.add("hidden");
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  setupColorDropdowns() {
    if (!this.querySelector('.product-form__input--dropdown select')) return;
    if (typeof ColorDropdownEnhancer !== 'function') return;
    this.colorDropdownEnhancer = new ColorDropdownEnhancer(this);
    this.colorDropdownEnhancer.init();
  }

  syncColorDropdowns() {
    if (!this.colorDropdownEnhancer) return;
    this.colorDropdownEnhancer.syncAll();
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
      }
    });
  }

  updateOptions() {
    const fieldsets = this.getOptionWrappers('fieldset');
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });

    this.updateLegends();
  }

  updateLegends() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    fieldsets.forEach((fieldset) => {
      const legend = fieldset.querySelector('legend [data-variant-legend]');
      const selectedOption = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked);
      legend.textContent = selectedOption.getAttribute('value');
    });
  }
}

customElements.define('variant-radios', VariantRadios);

class ProductBuyBar extends HTMLElement {
  constructor() {
    "use strict";
    super();

    let classes = { show: "show" };
    let mainProductForm = document.querySelector(".product-form");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // In view -> Hide bar
            this.classList.remove(classes.show);
          } else if (window.scrollY > 200) {
            // Out of view AND scrolled a little from top -> Show bar
            this.classList.add(classes.show);
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(mainProductForm);

    // This script is loaded in product-template.liquid --> Footer observer after DOM ready
    document.addEventListener("DOMContentLoaded", function () {
      let siteFooter = document.querySelector("footer.footer");
      if (!siteFooter) return;
      observer.observe(siteFooter);
    });
  }
}
customElements.define("product-buy-bar", ProductBuyBar);

class UpsellCard extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.image = this.querySelector('.card-media img');

    if (this.select) {
      this.select.addEventListener('change', (event) => {
        const selectedOption = event.target.options[event.target.selectedIndex];
        const newImgSrc = selectedOption.getAttribute('data-variant-img-src');
        if (newImgSrc) { this.updateImage(newImgSrc) }
      });
    }
  }

  updateImage(newImgSrc) {
    if (this.image) {
      this.image.src = newImgSrc;
    }
  }
}

customElements.define('upsell-card', UpsellCard);